<template>
  <div class="about-page">
    <div class="container-fluid py-5">
      <div class="ab-inner">
        <!-- Top: title area -->
        <header class="ab-header">
          <div class="ab-kicker">ABOUT</div>
          <h1 class="ab-title">BioFlow Studio</h1>
          <p class="ab-subtitle">
            A workflow studio for bioinformatics teams that connects presets, batch construction,
            file binding, and result exploration into a clear, end-to-end loop.
          </p>

          <div class="ab-actions">
            <button class="btn btn-sm btn-outline-secondary" @click="goStore">Preset Store</button>
            <button class="btn btn-sm btn-primary" @click="goBatch">Start Building</button>
          </div>
        </header>

        <!-- Main content: two sections -->
        <main class="ab-main">
          <!-- Features & workflow -->
          <section class="ab-section">
            <h2 class="ab-section-title">What does BioFlow Studio do?</h2>
            <p class="ab-text">
              BioFlow Studio aims to turn everyday bioinformatics workflows into
              <span class="ab-strong">readable, reusable, and shareable</span>
              “artifacts”.
              You can discover classic pipeline skeletons in the preset store, customize input types,
              steps, and parameters on the batch construction page, bind real files to abstract input keys
              in Step 3, and then submit the task to the backend with a single click.
              The frontend focuses on presenting runtime logs, progress tables, and image outputs
              in the most intuitive way possible in the Results Center,
              allowing team members who understand both bioinformatics and product design
              to communicate, refine, and improve pipelines within a single interface.
            </p>
          </section>

          <!-- Outlook & the AI era -->
          <section class="ab-section">
            <h2 class="ab-section-title">How do we view bioinformatics workflows in the AI era?</h2>
            <p class="ab-text">
              As AI continues to grow more capable, we envision BioFlow Studio as a
              <span class="ab-strong">“co-pilot with rails”</span>:
              users can describe their needs in natural language at a high level,
              letting models help draft pipeline skeletons and parameter suggestions,
              while the foundation remains solidly supported by clear input/output contracts,
              traceable configurations, and reproducible execution environments.
              For us, AI is not about replacing bioinformatics engineers,
              but about reducing mechanical repetition and enabling people to focus on what truly matters—
              designing better analysis strategies, understanding the biological questions behind the results,
              and distilling these insights into pipelines that can be reused by the team over the long term.
            </p>
          </section>
        </main>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AboutPage",
  methods: {
    goStore() {
      // Change to your actual route path, e.g. /home /store, etc.
      this.$router.push({ path: "/home" }).catch(() => {});
    },
    goBatch() {
      this.$router.push({ path: "/batch" }).catch(() => {});
    },
  },
};
</script>

<style scoped>
/* ===== Page wrapper: do not use pure white, match the global App background ===== */
.about-page{
  min-height: 100vh;
  background: transparent; /* Key: let the global gradient background from App.vue show through */
  color: rgba(15,23,42,0.92);
}

/* Content width */
.ab-inner{
  max-width: 960px;
  margin: 0 auto;
}

/* ===== Top header: frosted white (not pure white) ===== */
.ab-header{
  padding: 18px 16px 16px;
  border-radius: 18px;
  margin-bottom: 18px;

  /* Frosted canvas: softer than #fff */
  background:
    linear-gradient(180deg, rgba(255,255,255,0.50), rgba(255,255,255,0.28)),
    rgba(248,250,252,0.78);

  border: 1px solid rgba(15,23,42,0.06);
  box-shadow: 0 12px 36px rgba(15,23,42,0.06);

  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.ab-kicker{
  font-size: 12px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-weight: 950;
  color: rgba(15,23,42,0.55);
}

.ab-title{
  margin-top: 8px;
  font-size: 36px;
  font-weight: 950;
  letter-spacing: -0.035em;
  color: rgba(15,23,42,0.92);
}

.ab-subtitle{
  margin-top: 10px;
  font-size: 15px;
  line-height: 1.9;
  color: rgba(15,23,42,0.70);
  max-width: 46rem;
}

.ab-actions{
  margin-top: 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

/* ===== Main body: lighter frosted paper feel ===== */
.ab-main{
  border-radius: 18px;

  background:
    linear-gradient(180deg, rgba(255,255,255,0.40), rgba(255,255,255,0.22)),
    rgba(248,250,252,0.66);

  border: 1px solid rgba(15,23,42,0.05);
  box-shadow: 0 10px 32px rgba(15,23,42,0.05);

  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  padding: 18px 18px 6px;
}

/* Spacing between sections */
.ab-section{
  margin-bottom: 26px;
}

.ab-section-title{
  font-size: 18px;
  font-weight: 950;
  letter-spacing: -0.01em;
  margin-bottom: 10px;
  color: rgba(15,23,42,0.92);
}

.ab-text{
  font-size: 15px;
  color: rgba(15,23,42,0.74);
  line-height: 2.05;
  margin: 0;
}

.ab-strong{
  font-weight: 800;
  color: rgba(15,23,42,0.92);
}

/* Responsive */
@media (max-width: 768px){
  .ab-header{
    padding: 16px 14px 14px;
    border-radius: 16px;
  }
  .ab-main{
    padding: 16px 14px 6px;
    border-radius: 16px;
  }
  .ab-title{
    font-size: 30px;
  }
  .ab-subtitle{
    max-width: none;
    font-size: 14px;
  }
  .ab-text{
    font-size: 14px;
  }
}
</style>

